import { Link } from 'react-router-dom';
import { ArrowRight } from 'lucide-react';

export default function Module6Page() {
  return (
    <div className="min-h-screen bg-[var(--bg-raised)]">
      <header className="bg-[var(--green-dim)] border-b border-[var(--green)]">
        <div className="max-w-7xl mx-auto px-6 py-6">
          <Link to="/elevation/workbook/oqf" className="text-[var(--green)] dark:text-[var(--text-ter)] hover:text-[var(--green)] dark:hover:text-[var(--text-ter)] text-sm mb-4 inline-block">
            ← Back to OQF Home
          </Link>
          <h1 className="text-3xl font-bold text-[var(--text-pri)]">
            Module 6: Measure Clarity Acceleration
          </h1>
          <p className="text-[var(--text-sec)] mt-2">
            Objective: By the end of this module, you will be able to calculate and interpret the <strong>3 core metrics of clarity</strong> to prove the value of your learning.
          </p>
        </div>
      </header>

      <div className="max-w-7xl mx-auto px-6 py-8">
        <div className="bg-[var(--green-dim)] border border-[var(--green)] p-6 mb-8">
          <h2 className="text-xl font-semibold text-[var(--text-pri)] mb-4">
            💡 Concept: Measuring What Matters
          </h2>
          <p className="text-[var(--text-sec)] mb-4">
            This module proves the <em>value</em> of the OQF. We measure our success not by how many features we ship, but by how fast we are learning and turning unknowns into knowns.
          </p>
          <ul className="space-y-2 text-[var(--text-sec)]">
            <li><strong>Decision Latency (DL)</strong> shows <strong>speed</strong> of resolution.</li>
            <li><strong>Evidence Upgrade Rate (EUR)</strong> shows <strong>depth</strong> of learning.</li>
            <li><strong>Freshness Rate (FR)</strong> shows <strong>discipline</strong> of review.</li>
          </ul>
        </div>

        <div className="bg-[var(--bg-raised)] border border-[var(--rule)] p-6 mb-8">
          <h2 className="text-xl font-semibold text-[var(--text-pri)] mb-4">
            🛠️ Tool: The Core Metrics
          </h2>
          <div className="space-y-6">
            <div>
              <h3 className="font-semibold text-[var(--text-pri)] mb-2">1. Decision Latency (DL)</h3>
              <ul className="list-disc list-inside text-[var(--text-sec)] space-y-1">
                <li><strong>What it is:</strong> The average time from when a question is <em>created</em> to when it is <em>answered</em> ("Confirmed").</li>
                <li><strong>Calculation:</strong> (Sum of days to resolve all questions) / (# of questions resolved)</li>
              </ul>
            </div>
            <div>
              <h3 className="font-semibold text-[var(--text-pri)] mb-2">2. Evidence Upgrade Rate (EUR)</h3>
              <ul className="list-disc list-inside text-[var(--text-sec)] space-y-1">
                <li><strong>What it is:</strong> The percentage of active questions that improved by at least one tier on the Evidence Scale (e.g., "Anecdote" → "Directional") in a given period.</li>
                <li><strong>Calculation:</strong> (# of questions that upgraded) / (Total # of active questions)</li>
              </ul>
            </div>
            <div>
              <h3 className="font-semibold text-[var(--text-pri)] mb-2">3. Freshness Rate (FR)</h3>
              <ul className="list-disc list-inside text-[var(--text-sec)] space-y-1">
                <li><strong>What it is:</strong> The percentage of questions that were reviewed on or before their "Review Due" date.</li>
                <li><strong>Calculation:</strong> (# of questions reviewed on time) / (Total # of questions due for review)</li>
              </ul>
            </div>
          </div>
        </div>

        <div className="flex gap-4">
          <Link to="/elevation/workbook/oqf/module-5" className="px-4 py-2 border border-[var(--rule)] text-[var(--text-sec)] hover:bg-[var(--surface)]">
            ← Previous Module
          </Link>
          <Link to="/elevation/workbook/oqf/module-7" className="px-4 py-2 bg-[var(--green)] text-white hover:bg-[var(--green)] flex items-center gap-2">
            Next: Module 7
            <ArrowRight className="w-4 h-4" />
          </Link>
        </div>
      </div>
    </div>
  );
}






